import React from "react";
import { AlertTriangle, ChevronLeft } from "lucide-react";

const questionPreview = (text = "", index) => {
  const flat = String(text).replace(/\s+/g, " ").trim();
  if (!flat) return `Question ${index + 1}`;
  return flat.length > 110 ? `${flat.slice(0, 107)}…` : flat;
};

const SAVE_CHIPS = {
  pending: "Saving…",
  saving: "Saving…",
  failed: "Not saved",
  conflict: "Changed in another window",
};

// Answer overview before final submission. Unanswered questions are listed
// but never block submitting. Unsaved drawing drafts do: the student saves
// or discards each one first.
const AssessmentReview = ({
  questions,
  answeredIds,
  saveStates,
  submitState,
  deadlineClosed,
  headingRef,
  onSelect,
  onBack,
  onSubmit,
  drawingIds = new Set(),
  draftIds = [],
  drawingBusy = {},
  onOpenDrawing,
  onDiscardDraft,
}) => {
  const total = questions.length;
  const answered = questions.filter((question) => answeredIds.has(question.id)).length;
  const unanswered = total - answered;
  const submitting = submitState.status === "submitting";
  const unsavedCount = submitState.status === "unsaved" ? submitState.unsavedIds.length : 0;
  const draftSet = new Set(draftIds);
  const pendingDrafts = questions
    .map((question, index) => ({ question, index }))
    .filter(({ question }) => draftSet.has(question.id));

  return (
    <div className="eq-assess-review">
      <p className="eq-assess-kicker">Review answers</p>
      <h2 className="eq-assess-review-title" tabIndex={-1} ref={headingRef}>Check your answers before submitting</h2>
      <p className="eq-assess-review-summary">
        {answered} of {total} questions answered.
        {unanswered > 0
          ? ` ${unanswered} ${unanswered === 1 ? "question is" : "questions are"} unanswered — you can still submit, or go back and answer ${unanswered === 1 ? "it" : "them"}.`
          : " Every question has an answer."}
      </p>

      <ol className="eq-assess-review-list">
        {questions.map((question, index) => {
          const isAnswered = answeredIds.has(question.id);
          const chip = SAVE_CHIPS[saveStates[question.id]];
          return (
            <li key={question.id} className={isAnswered ? "is-answered" : "is-unanswered"}>
              <span className="eq-assess-review-number" aria-hidden="true">{index + 1}</span>
              <div className="eq-assess-review-text">
                <span className="eq-assess-review-question">{questionPreview(question.questionText, index)}</span>
                <span className="eq-assess-review-chips">
                  <span className={`eq-assess-chip ${isAnswered ? "is-answered" : "is-unanswered"}`}>
                    {isAnswered ? "Answered" : "Unanswered"}
                  </span>
                  {chip && <span className={`eq-assess-chip is-${saveStates[question.id]}`}>{chip}</span>}
                  {drawingIds.has(question.id) && <span className="eq-assess-chip is-drawing">Drawing</span>}
                  {draftSet.has(question.id) && <span className="eq-assess-chip is-pending">Unsaved drawing</span>}
                </span>
              </div>
              <button
                type="button"
                className="eq-assess-link-button"
                onClick={() => onSelect(index)}
                aria-label={`Go to question ${index + 1}`}
                disabled={submitting}
              >
                Go to question
              </button>
            </li>
          );
        })}
      </ol>

      {submitState.status === "unsaved" && (
        <div className="eq-assess-submit-alert is-warning" role="alert">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <strong>
              {unsavedCount} {unsavedCount === 1 ? "answer" : "answers"} could not be saved.
            </strong>
            <p>
              Retry saving, or submit the answers that are already saved. Changes on this page
              that are not saved will not be included.
            </p>
            <div className="eq-assess-submit-alert-actions">
              <button type="button" className="eq-assess-secondary" onClick={() => onSubmit()}>Retry saving</button>
              <button type="button" className="eq-assess-secondary" onClick={() => onSubmit({ savedOnly: true })}>
                Submit saved answers
              </button>
            </div>
          </div>
        </div>
      )}

      {submitState.status === "drawing-drafts" && pendingDrafts.length > 0 && (
        <div className="eq-assess-submit-alert is-warning" role="alert">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <strong>
              {pendingDrafts.length} unsaved {pendingDrafts.length === 1 ? "drawing needs" : "drawings need"} a decision before you submit.
            </strong>
            <p>Open each drawing and choose Save drawing to include it, or discard it. Unsaved drafts are not submitted when you finish.</p>
            <ul className="eq-assess-draft-list">
              {pendingDrafts.map(({ question, index }) => (
                <li key={question.id}>
                  <span>Question {index + 1}</span>
                  <button type="button" className="eq-assess-secondary" onClick={() => onOpenDrawing(index)}
                    disabled={Boolean(drawingBusy[question.id])}>
                    Open drawing
                  </button>
                  <button type="button" className="eq-assess-secondary" onClick={() => onDiscardDraft(question.id)}
                    disabled={Boolean(drawingBusy[question.id])}>
                    {drawingBusy[question.id] === "discarding" ? "Discarding…" : "Discard draft"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {submitState.status === "error" && (
        <div className="eq-assess-submit-alert is-error" role="alert">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <strong>Your assessment has not been submitted yet.</strong>
            <p>{submitState.message}</p>
          </div>
        </div>
      )}

      <div className="eq-assess-review-actions">
        <button type="button" className="eq-assess-secondary" onClick={onBack} disabled={submitting}>
          <ChevronLeft size={19} aria-hidden="true" />Back to questions
        </button>
        <button
          type="button"
          className="eq-submit-btn eq-assess-next"
          onClick={() => onSubmit()}
          disabled={submitting || deadlineClosed}
          aria-busy={submitting}
        >
          {submitting ? "Submitting…" : submitState.status === "error" ? "Try again" : "Submit & Finish"}
        </button>
      </div>
    </div>
  );
};

export default AssessmentReview;
